/* eslint-disable react/jsx-props-no-spreading */
import {
    useCallback, useEffect, useRef, useState,
} from 'react'
import {
    QuickFilterClear, QuickFilterControl, QuickFilterTrigger,
} from '@mui/x-data-grid-pro'

import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import Tooltip from '@mui/material/Tooltip'
import CancelIcon from '@mui/icons-material/Cancel'
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    useResizeObserver,
} from 'usehooks-ts'

import {
    noop,
} from 'lodash'
import {
    StyledQuickFilter,
    StyledTextField,
    StyledToolbarButton,
    StyledWrapper,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/components/SkyBaseDataGridToolbar/QuickFilterToolbarItem/QuickFilterToolbarItem.styled'

export const MAX_EXPANDED_INPUT_WIDTH = 300

export default function QuickFilterToolbarItem({
    useDynamicWidth = false,
    defaultInputWidth = MAX_EXPANDED_INPUT_WIDTH,
    onExpand = noop,
    onContainerResize = noop,
}: Readonly<{
    useDynamicWidth?: boolean,
    defaultInputWidth?: number
    onExpand?: (expanded: boolean, maxWidth: number) => void
    onContainerResize?: (width: number) => void
}>) {
    const containerRef = useRef<HTMLDivElement>(null)
    const {
        width,
    } = useResizeObserver({
        ref: containerRef,
    })

    const [
        containerWidth,
        setContainerWidth,
    ] = useState(defaultInputWidth)

    const handleExpandCollapse = useCallback((isExpanded: boolean) => {
        onExpand(isExpanded, containerWidth)
    }, [
        containerWidth,
        onExpand,
    ])

    useEffect(() => {
        if (width && useDynamicWidth) {
            setContainerWidth(width)
            onContainerResize(width)
        }
    }, [
        width,
        useDynamicWidth,
        onContainerResize,
        handleExpandCollapse,
    ])

    return (
        <StyledWrapper ref={containerRef}>
            <StyledQuickFilter
                onExpandedChange={handleExpandCollapse}
            >
                <QuickFilterTrigger
                    render={(triggerProps, state) => {
                        return (
                            <Tooltip
                                title="Search"
                                enterDelay={0}
                            >
                                <StyledToolbarButton
                                    {...triggerProps}
                                    ownerState={{
                                        expanded: state.expanded,
                                    }}
                                    ref={(node) => {
                                        // it is needed for proper positioning
                                        (triggerProps.ref as (el) => void)(node)
                                    }}
                                    color="default"
                                    aria-disabled={state.expanded}
                                    data-testid="search-filter-btn"
                                >
                                    <SearchIcon fontSize="small" />
                                </StyledToolbarButton>
                            </Tooltip>
                        )
                    }}
                />
                <QuickFilterControl
                    render={({
                        ref: inputRef, ...controlProps
                    }, state) => {
                        return (
                            <StyledTextField
                                {...controlProps}
                                ownerState={{
                                    expanded: state.expanded,
                                }}
                                inputRef={inputRef}
                                aria-label="Search"
                                placeholder="Search..."
                                size="small"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <QuickFilterClear
                                                    edge="end"
                                                    size="small"
                                                    aria-label="Clear search"
                                                    material={{
                                                        sx: {
                                                            marginRight: -0.75,
                                                            visibility: state.value ? 'visible' : 'hidden',
                                                        },
                                                    }}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </QuickFilterClear>
                                            </InputAdornment>),
                                        ...controlProps.slotProps?.input,
                                    },
                                    ...controlProps.slotProps,
                                }}
                            />
                        )
                    }}
                />
            </StyledQuickFilter>
        </StyledWrapper>
    )
}
